/** Provider-neutral WebRTC media with authenticated application events. */
export type LiveCallUpdate = {
  phase: "listening" | "thinking" | "speaking";
  caption: string;
  heard: string;
  exchanges: Array<{ role: "user" | "bot"; text: string }>;
};
export type LiveCall = { mute(value: boolean): void; close(): void };
export type LiveCallOptions = {
  botId: string;
  callId: string;
  baseUrl?: string;
  headers?: HeadersInit;
  signal: AbortSignal;
  onState(state: LiveCallUpdate): void;
  muted?: () => boolean;
  onMedia?: (control: LiveCall) => void;
  onClose(): void;
  onError(error: Error): void;
};

export function supportsLiveCall() {
  return typeof RTCPeerConnection !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia);
}

export async function startLiveCall(options: LiveCallOptions): Promise<LiveCall | null> {
  if (!supportsLiveCall()) return null;
  const base = options.baseUrl ?? window.location.origin;
  const headers = new Headers(options.headers);
  headers.set("content-type", "application/json");
  const api = (path: string, init: RequestInit = {}) =>
    fetch(`${base}/api/voice/live${path}`, {
      ...init,
      headers,
      credentials: "include",
      signal: init.signal ?? options.signal,
    });
  const capability = await api(`/capability?botId=${encodeURIComponent(options.botId)}`);
  if (capability.status === 404) return null;
  if (!capability.ok) throw new Error("Could not connect the call.");
  if (!(await capability.json()).supported) return null;
  options.signal.throwIfAborted();
  const peer = new RTCPeerConnection();
  // A data channel is required for the media session; control events are owned by our server.
  const channel = peer.createDataChannel("events");
  const audio = new Audio();
  audio.autoplay = true;
  audio.setAttribute("playsinline", "");
  let microphone: MediaStream | undefined;
  let id: string | undefined;
  let closed = false;
  const feed = new AbortController();
  const close = () => {
    if (closed) return;
    closed = true;
    options.signal.removeEventListener("abort", close);
    microphone?.getTracks().forEach((track) => {
      track.stop();
    });
    audio.pause();
    audio.srcObject = null;
    channel.close();
    peer.close();
    feed.abort();
    if (id)
      void api(`/${encodeURIComponent(id)}`, {
        method: "DELETE",
        signal: AbortSignal.timeout(5000),
      }).catch(() => undefined);
  };
  options.signal.addEventListener("abort", close, { once: true });
  const fail = (error: Error) => {
    if (!closed) {
      close();
      options.onError(error);
    }
  };
  peer.addEventListener("track", (event) => {
    if (closed) return;
    audio.srcObject = new MediaStream([event.track]);
    void audio.play().catch(() => fail(new Error("Could not play call audio.")));
  });
  peer.addEventListener("connectionstatechange", () => {
    if (peer.connectionState === "failed" || peer.connectionState === "disconnected")
      fail(new Error("The call disconnected."));
  });
  try {
    microphone = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    if (closed || options.signal.aborted) {
      microphone.getTracks().forEach((track) => {
        track.stop();
      });
      throw new Error("Call cancelled.");
    }
    const mediaControl: LiveCall = {
      close,
      mute(value) {
        microphone?.getAudioTracks().forEach((track) => {
          track.enabled = !value;
        });
      },
    };
    mediaControl.mute(options.muted?.() ?? false);
    options.onMedia?.(mediaControl);
    for (const track of microphone.getAudioTracks()) peer.addTrack(track, microphone);
    await peer.setLocalDescription(await peer.createOffer());
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => done(new Error("Call connection timed out.")), 10_000);
      const changed = () => {
        if (peer.iceGatheringState === "complete") done();
      };
      const aborted = () => done(new Error("Call cancelled."));
      function done(error?: Error) {
        clearTimeout(timer);
        peer.removeEventListener("icegatheringstatechange", changed);
        options.signal.removeEventListener("abort", aborted);
        if (error) reject(error);
        else resolve();
      }
      peer.addEventListener("icegatheringstatechange", changed);
      options.signal.addEventListener("abort", aborted, { once: true });
      changed();
    });
    const response = await api("", {
      method: "POST",
      body: JSON.stringify({
        botId: options.botId,
        callId: options.callId,
        offer: peer.localDescription?.sdp,
      }),
      signal: AbortSignal.any([options.signal, AbortSignal.timeout(25_000)]),
    });
    if (!response.ok) throw new Error("Could not connect the live call.");
    const result = (await response.json()) as { supported: boolean; id: string; answer: string };
    if (!result.supported) {
      close();
      return null;
    }
    id = result.id;
    if (closed || options.signal.aborted) {
      void api(`/${encodeURIComponent(id)}`, {
        method: "DELETE",
        signal: AbortSignal.timeout(5000),
      }).catch(() => undefined);
      throw new Error("Call cancelled.");
    }
    await peer.setRemoteDescription({ type: "answer", sdp: result.answer });
    void (async () => {
      const response = await api(`/${encodeURIComponent(id!)}/events`, { signal: feed.signal });
      if (!response.ok || !response.body) throw new Error("Call connection lost.");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          if (buffer.length > 256_000) throw new Error("Invalid call update.");
          while (buffer.includes("\n\n")) {
            const end = buffer.indexOf("\n\n");
            const lines = buffer.slice(0, end).split("\n");
            buffer = buffer.slice(end + 2);
            const data = lines
              .filter((line) => line.startsWith("data:"))
              .map((line) => line.slice(5).trimStart())
              .join("\n");
            if (!data) continue;
            const event = JSON.parse(data);
            if (event.type === "state") options.onState(event as LiveCallUpdate);
            else if (event.type === "closed") {
              close();
              options.onClose();
              return;
            }
          }
        }
        if (!closed) throw new Error("Call connection lost.");
      } finally {
        await reader.cancel().catch(() => undefined);
        reader.releaseLock();
      }
    })().catch((error) => {
      if (!closed) fail(error instanceof Error ? error : new Error("Call connection lost."));
    });
    return {
      close,
      mute(value) {
        microphone?.getAudioTracks().forEach((track) => {
          track.enabled = !value;
        });
      },
    };
  } catch (error) {
    close();
    throw error;
  }
}
